import { useState, useEffect, useCallback } from 'react';
import { monitoringApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Activity,
  Database,
  Server,
  Cloud,
  Clock,
  RefreshCw,
  HardDrive,
  FileText,
  ShoppingCart,
  Package,
  Users,
  BarChart3,
  Receipt,
  Truck,
  Bell,
  BellOff,
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  Settings2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

const StatusDot = ({ status }) => {
  const colors = {
    ok: 'bg-emerald-500 shadow-emerald-500/40',
    warning: 'bg-amber-500 shadow-amber-500/40',
    error: 'bg-red-500 shadow-red-500/40',
    inactive: 'bg-slate-400 shadow-slate-400/20',
  };
  return (
    <span
      data-testid={`status-dot-${status}`}
      className={`inline-block w-2.5 h-2.5 rounded-full shadow-lg ${colors[status] || colors.inactive}`}
    />
  );
};

const formatDateTime = (isoStr) => {
  if (!isoStr) return 'Nunca';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoStr;
  }
};

const formatNumber = (n) => {
  if (n === undefined || n === null) return '0';
  return new Intl.NumberFormat('es-CL').format(n);
};

const SEVERITY_CONFIG = {
  error: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30', badge: 'bg-red-500' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30', badge: 'bg-amber-500' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30', badge: 'bg-blue-500' },
};

const THRESHOLD_LABELS = {
  alegra_sync_hours: { label: 'Alegra sync atrasada', unit: 'horas', description: 'Horas sin sincronización antes de alertar' },
  facturas_pendientes_dias: { label: 'Facturas pendientes', unit: 'días', description: 'Días que una factura puede estar sin contabilizar' },
  access_file_dias: { label: 'Archivo Access antiguo', unit: 'días', description: 'Días sin actualizar el archivo .mdb' },
  dte_sin_procesar_max: { label: 'DTEs sin procesar', unit: 'máx', description: 'Cantidad máxima de DTEs pendientes' },
  recepciones_pendientes_max: { label: 'Recepciones pendientes', unit: 'máx', description: 'Cantidad máxima de OCs sin recepción completa' },
};

const AlertCard = ({ alert, onDismiss }) => {
  const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
  const Icon = sev.icon;
  return (
    <div data-testid={`alert-${alert.id}`} className={`flex items-start gap-3 p-3 rounded-lg border ${sev.bg} transition-all hover:scale-[1.01]`}>
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${sev.color}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-200">{alert.title}</p>
        <p className="text-xs text-slate-400 mt-0.5">{alert.message}</p>
      </div>
      <button
        data-testid={`dismiss-alert-${alert.id}`}
        onClick={() => onDismiss(alert.id)}
        className="shrink-0 p-1 rounded hover:bg-slate-700/50 text-slate-500 hover:text-slate-300 transition-colors"
        title="Descartar por 24h"
      >
        <BellOff className="w-4 h-4" />
      </button>
    </div>
  );
};

export default function MonitoreoSistema() {
  const [health, setHealth] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [moduleStats, setModuleStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertConfig, setAlertConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [editThresholds, setEditThresholds] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [healthRes, dbRes, moduleRes, activityRes, alertsRes] = await Promise.all([
        monitoringApi.getHealth(),
        monitoringApi.getDbStats(),
        monitoringApi.getModuleStats(),
        monitoringApi.getActivity(20),
        monitoringApi.getAlerts(),
      ]);
      setHealth(healthRes.data);
      setDbStats(dbRes.data);
      setModuleStats(moduleRes.data);
      setActivity(activityRes.data.activities || []);
      setAlerts(alertsRes.data.alerts || []);
      setLastRefresh(new Date());
    } catch (error) { console.error('caught:', error); } finally {
      setLoading(false);
      setRefreshing(false);
    }
   
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => loadData(), 300000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = async (alertId) => {
    try {
      await monitoringApi.dismissAlert(alertId, 24);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      toast.success('Alerta descartada por 24 horas');
    } catch {
      toast.error('Error al descartar alerta');
    }
  };

  const openConfig = async () => {
    try {
      const res = await monitoringApi.getAlertConfig();
      setAlertConfig(res.data);
      setEditThresholds({ ...res.data.thresholds });
      setShowConfig(true);
    } catch {
      toast.error('Error cargando configuración');
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await monitoringApi.updateAlertConfig(editThresholds);
      toast.success('Umbrales actualizados');
      setShowConfig(false);
      loadData(true);
    } catch {
      toast.error('Error guardando configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="monitoring-loading">
        <div className="flex items-center gap-3">
          <Activity className="w-7 h-7 text-cyan-500" />
          <h1 className="text-2xl font-bold text-slate-100">Monitoreo del Sistema</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-slate-800/50 border-slate-700 animate-pulse">
              <CardContent className="p-6 h-28" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const serviceCards = [
    {
      title: 'Base de Datos',
      icon: Database,
      status: health?.database?.connected ? 'ok' : 'error',
      detail: health?.database?.connected ? 'Conectada' : 'Desconectada',
      extra: dbStats ? `${formatNumber(dbStats.total_documents)} documentos` : null,
      color: 'text-cyan-400',
    },
    {
      title: 'Sincronización Alegra',
      icon: Cloud,
      status: health?.alegra_sync?.enabled ? 'ok' : 'inactive',
      detail: health?.alegra_sync?.enabled
        ? `Programada ${health.alegra_sync.schedule}`
        : 'Deshabilitada',
      extra: health?.alegra_sync?.last_sync
        ? `Última: ${formatDateTime(health.alegra_sync.last_sync)}`
        : 'Sin sincronización previa',
      color: 'text-violet-400',
    },
    {
      title: 'Informe Gerencial',
      icon: BarChart3,
      status: health?.gerencia_report?.enabled ? 'ok' : 'inactive',
      detail: health?.gerencia_report?.enabled
        ? `Programado ${health.gerencia_report.schedule}`
        : 'Deshabilitado',
      extra: health?.gerencia_report?.last_sent
        ? `Último: ${formatDateTime(health.gerencia_report.last_sent)}`
        : 'Nunca enviado',
      color: 'text-amber-400',
    },
    {
      title: 'Archivo Access',
      icon: HardDrive,
      status: health?.access_file?.exists ? 'ok' : 'warning',
      detail: health?.access_file?.exists
        ? `${health.access_file.size_mb} MB`
        : 'No cargado',
      extra: health?.access_file?.modified
        ? `Actualizado: ${formatDateTime(health.access_file.modified)}`
        : null,
      color: 'text-emerald-400',
    },
  ];

  const moduleCards = moduleStats ? [
    { title: 'Órdenes de Trabajo', icon: FileText, value: moduleStats.ordenes_trabajo?.total || 0, sub: `${moduleStats.ordenes_trabajo?.en_ejecucion || 0} en ejecución`, color: 'from-blue-600/20 to-blue-800/10 border-blue-600/30' },
    { title: 'Órdenes de Compra', icon: ShoppingCart, value: moduleStats.compras?.total_ocs || 0, sub: `${moduleStats.compras?.aprobadas || 0} aprobadas`, color: 'from-violet-600/20 to-violet-800/10 border-violet-600/30' },
    { title: 'Documentos DTE', icon: Receipt, value: moduleStats.dte?.total || 0, sub: 'procesados', color: 'from-amber-600/20 to-amber-800/10 border-amber-600/30' },
    { title: 'Materiales', icon: Package, value: moduleStats.bodega?.materiales || 0, sub: `${formatNumber(moduleStats.bodega?.stock_items || 0)} items stock`, color: 'from-emerald-600/20 to-emerald-800/10 border-emerald-600/30' },
    { title: 'Recepciones', icon: Truck, value: moduleStats.recepciones?.total || 0, sub: 'registradas', color: 'from-cyan-600/20 to-cyan-800/10 border-cyan-600/30' },
    { title: 'Usuarios', icon: Users, value: moduleStats.usuarios?.total || 0, sub: `${moduleStats.usuarios?.activos || 0} activos`, color: 'from-pink-600/20 to-pink-800/10 border-pink-600/30' },
    { title: 'Alegra Facturas', icon: Receipt, value: moduleStats.alegra?.facturas || 0, sub: `${formatNumber(moduleStats.alegra?.compras || 0)} compras`, color: 'from-indigo-600/20 to-indigo-800/10 border-indigo-600/30' },
    { title: 'Contabilidad', icon: BarChart3, value: moduleStats.contabilidad?.facturas_pendientes || 0, sub: 'facturas pendientes', color: 'from-orange-600/20 to-orange-800/10 border-orange-600/30' },
  ] : [];

  const errorCount = alerts.filter(a => a.severity === 'error').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="p-6 space-y-6" data-testid="monitoring-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-cyan-500/10">
            <Activity className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Monitoreo del Sistema</h1>
            <p className="text-sm text-slate-400">
              {lastRefresh ? `Actualizado: ${lastRefresh.toLocaleTimeString('es-CL')}` : ''}
              {' \u00b7 Auto-refresh cada 60s'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="alert-config-btn"
            variant="outline"
            size="sm"
            onClick={openConfig}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <Settings2 className="w-4 h-4 mr-2" />
            Umbrales
          </Button>
          <Button
            data-testid="refresh-btn"
            variant="outline"
            size="sm"
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div data-testid="alerts-section">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-5 h-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-slate-200">
              Alertas Activas
            </h2>
            <div className="flex items-center gap-1.5 ml-2">
              {errorCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
                  {errorCount} {errorCount === 1 ? 'error' : 'errores'}
                </span>
              )}
              {warningCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  {warningCount} {warningCount === 1 ? 'aviso' : 'avisos'}
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {alerts.map((alert) => (
              <AlertCard key={alert.id} alert={alert} onDismiss={handleDismiss} />
            ))}
          </div>
        </div>
      )}

      {alerts.length === 0 && (
        <div data-testid="no-alerts" className="flex items-center gap-3 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <CheckCircle2 className="w-5 h-5 text-emerald-400" />
          <div>
            <p className="text-sm font-medium text-emerald-300">Sin alertas activas</p>
            <p className="text-xs text-slate-400">Todos los indicadores del sistema están dentro de los umbrales configurados</p>
          </div>
        </div>
      )}

      {/* Service Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="service-cards">
        {serviceCards.map((card) => (
          <Card key={card.title} className="bg-slate-800/60 border-slate-700/50 hover:border-slate-600/50 transition-colors">
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-slate-700/50">
                  <card.icon className={`w-5 h-5 ${card.color}`} />
                </div>
                <StatusDot status={card.status} />
              </div>
              <h3 className="text-sm font-medium text-slate-300 mb-1">{card.title}</h3>
              <p className="text-lg font-semibold text-slate-100">{card.detail}</p>
              {card.extra && (
                <p className="text-xs text-slate-500 mt-1">{card.extra}</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Module Statistics */}
      <div>
        <h2 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Server className="w-5 h-5 text-slate-400" />
          Estadísticas por Módulo
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="module-stats">
          {moduleCards.map((card) => (
            <div
              key={card.title}
              className={`rounded-xl bg-gradient-to-br ${card.color} border p-4 transition-transform hover:scale-[1.02]`}
            >
              <div className="flex items-center gap-2 mb-2">
                <card.icon className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">{card.title}</span>
              </div>
              <p className="text-2xl font-bold text-slate-100 tabular-nums">{formatNumber(card.value)}</p>
              <p className="text-xs text-slate-500 mt-0.5">{card.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Database Collections + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-200 flex items-center gap-2">
              <Database className="w-4 h-4 text-cyan-400" />
              Colecciones de Base de Datos
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="db-collections">
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-2">
              {dbStats?.collections?.map((col) => (
                <div key={col.collection} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-700/30 transition-colors">
                  <span className="text-sm text-slate-400">{col.label}</span>
                  <span className="text-sm font-mono font-medium text-slate-200 tabular-nums">
                    {formatNumber(col.count)}
                  </span>
                </div>
              ))}
            </div>
            {dbStats && (
              <div className="mt-3 pt-3 border-t border-slate-700/50 flex justify-between">
                <span className="text-sm font-medium text-slate-300">Total documentos</span>
                <span className="text-sm font-bold text-cyan-400 tabular-nums">{formatNumber(dbStats.total_documents)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-slate-800/60 border-slate-700/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-200 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-400" />
              Actividad Reciente
            </CardTitle>
          </CardHeader>
          <CardContent data-testid="recent-activity">
            {activity.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No hay actividad registrada aún</p>
                <p className="text-xs mt-1">La actividad se registrará automáticamente</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                {activity.map((act, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 px-2 rounded hover:bg-slate-700/30 transition-colors">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-500 mt-2 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-slate-300 truncate">{act.action || act.detail}</p>
                      <div className="flex gap-2 text-xs text-slate-500 mt-0.5">
                        <span>{act.user}</span>
                        <span>{formatDateTime(act.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Server Info */}
      {health?.server && (
        <div className="flex items-center gap-6 text-xs text-slate-600 border-t border-slate-800 pt-4">
          <span>Python {health.server.python_version}</span>
          <span>{health.server.os}</span>
          <span>{dbStats?.total_collections} colecciones monitoreadas</span>
        </div>
      )}

      {/* Config Modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowConfig(false)}>
          <div
            data-testid="alert-config-modal"
            className="bg-slate-800 border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <Settings2 className="w-5 h-5 text-cyan-400" />
                <h3 className="text-lg font-semibold text-slate-100">Configurar Umbrales de Alerta</h3>
              </div>
              <button onClick={() => setShowConfig(false)} className="p-1 rounded hover:bg-slate-700 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
              {Object.entries(THRESHOLD_LABELS).map(([key, meta]) => (
                <div key={key} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-slate-300">{meta.label}</label>
                    <span className="text-xs text-slate-500">
                      (Default: {alertConfig?.defaults?.[key]} {meta.unit})
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">{meta.description}</p>
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid={`threshold-${key}`}
                      type="number"
                      min={0}
                      value={editThresholds[key] || ''}
                      onChange={(e) => setEditThresholds((prev) => ({ ...prev, [key]: parseInt(e.target.value) || 0 }))}
                      className="bg-slate-900 border-slate-600 text-slate-200 w-24"
                    />
                    <span className="text-sm text-slate-400">{meta.unit}</span>
                    {editThresholds[key] !== alertConfig?.defaults?.[key] && (
                      <button
                        className="text-xs text-cyan-400 hover:underline"
                        onClick={() => setEditThresholds((prev) => ({ ...prev, [key]: alertConfig.defaults[key] }))}
                      >
                        Restaurar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-700">
              <Button variant="outline" size="sm" onClick={() => setShowConfig(false)} className="border-slate-600 text-slate-300">
                Cancelar
              </Button>
              <Button
                data-testid="save-thresholds-btn"
                size="sm"
                onClick={saveConfig}
                disabled={savingConfig}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
              >
                {savingConfig ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
